import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { FloatLabelType, MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { isValidPhoneNumber } from '@domains';
import { NgxMaskDirective } from 'ngx-mask';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';

/**
 * RP-4072: the one phone field. Drops into an existing reactive form in place of the
 * hand-rolled `mat-form-field` + masked `<input>` block that is copied ~30 times across
 * this app, and brings the mask, the NANP rule (`@domains` isValidPhoneNumber, the same
 * one the donor webflow enforces) and the error copy with it — so the next phone field
 * cannot ship without validation, and the message reads the same everywhere.
 *
 * It is a ControlValueAccessor AND a Validator: `formControlName="phone"` is all a form
 * needs, and the outer control goes invalid on a bad number without the parent adding a
 * validator of its own. NOTE for the surfaces: Angular MERGES this validator into the
 * control's own list, so a later `control.setValidators(...)` on that control would drop
 * it — use `addValidators`/`removeValidators` when toggling requiredness at runtime.
 */
@Component({
  selector: 'rspl-phone-input',
  templateUrl: './phone-input.component.html',
  // mat-form-field is inline-flex, so on its own it shrink-wraps to the mask's width while
  // the sibling Name/Email fields (which carry fxFlex at their call sites) fill the row.
  styles: `
    :host {
      display: block;
      flex: 1 1 auto;
      /* Room for the error line. Constant, so the message appearing doesn't shove the
         next field down — this app's rows sit tight enough that the default subscript
         space alone lets the text land on the following field's label. */
      padding-bottom: 18px;
    }
    mat-form-field {
      width: 100%;
    }
  `,
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatError, MatInput, NgxMaskDirective],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneInputComponent), multi: true },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, Validator, OnDestroy {
  @Input() label = 'Phone';
  @Input() readonly = false;
  @Input() floatLabel: FloatLabelType = 'auto';
  /**
   * Optional E2E hook. This input is reused ~30× (four times on one contacts tab), so a
   * static testid on the field would collide — the caller names each instance instead.
   * Attribute-only: renders no attribute when unset, so no existing surface is affected.
   */
  @Input() testId?: string;

  /**
   * Changeable at runtime — the store form flips it with the "Accepts Courier" checkbox.
   * Re-validating and announcing the change is what keeps the error state honest: a
   * validator's result is cached until something re-runs it, so without this the field
   * would still report "fine" under the previous requiredness.
   */
  @Input()
  set required(value: boolean) {
    this.isRequired = value;
    this.inner.updateValueAndValidity({ emitEvent: false });
    this.onValidatorChange();
  }
  get required(): boolean {
    return this.isRequired;
  }

  /**
   * The value lives on an inner control, not on a plain input, so Material decides the
   * field's error state the way it does everywhere else (invalid + touched) and mat-error
   * renders without this component having to implement MatFormFieldControl.
   */
  readonly inner = new FormControl<string>('', { nonNullable: true, validators: (c) => this.check(c.value) });

  private isRequired = false;
  private readonly destroy$ = new Subject<void>();
  private outer?: AbstractControl;
  private outerTouched?: Subscription;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  constructor() {
    this.inner.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => this.onChange(value));
  }

  writeValue(value: string | null): void {
    this.inner.setValue(value ?? '', { emitEvent: false });
    this.flagStoredInvalid();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inner.disable({ emitEvent: false });
    } else {
      this.inner.enable({ emitEvent: false });
      // A disabled control reports no errors, so a form built disabled (and only enabled
      // when the user clicks Edit) would never have flagged its stored bad number.
      this.flagStoredInvalid();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // Track the OUTER control's touched state: Material derives the red state from the
    // inner one, so without this a form that marks everything touched on Save would leave
    // this field looking fine while refusing to submit. Both tabs that host this rebuild
    // their whole FormGroup on input changes, so the state is re-seeded (not just
    // subscribed) whenever the control instance is replaced — otherwise a stale `touched`
    // would keep the field red on a form nobody has touched.
    if (this.outer !== control) {
      this.outer = control;
      this.outerTouched?.unsubscribe();
      this.syncTouched(control.touched);
      this.outerTouched = control.events
        .pipe(
          filter((event): event is TouchedChangeEvent => event instanceof TouchedChangeEvent),
          takeUntil(this.destroy$),
        )
        .subscribe((event) => this.syncTouched(event.touched));
    }
    return this.check(control.value);
  }

  /**
   * Pasting a formatted or +1-prefixed number would otherwise be chewed up by the mask:
   * the leading "1" shifts every digit and the number silently becomes a different one.
   * Read off the clipboard rather than an `input` event, whose `data` is null for a paste.
   */
  onPaste(event: ClipboardEvent): void {
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!digits) return;
    event.preventDefault();
    this.inner.setValue(digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits);
  }

  onBlur(): void {
    this.inner.markAsTouched();
    this.onTouched();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * A stored number that fails the rule is flagged on arrival, not on blur or on Save. The
   * field already blocks the save, so hiding the reason until the user pokes it makes the
   * form look fine and behave broken — and this lands on a lot of legacy bogus numbers.
   * Only the INNER control is touched (Material reads the red state off it), so the parent
   * form's own pristine/touched reporting stays honest, and an empty value stays quiet:
   * absent is allowed, wrong is not.
   */
  private flagStoredInvalid(): void {
    if (this.inner.invalid && this.inner.value) {
      this.inner.markAsTouched();
    }
  }

  private syncTouched(touched: boolean): void {
    if (touched) {
      this.inner.markAsTouched();
      return;
    }
    // An untouched control must not un-flag a stored number that is already wrong.
    if (this.inner.invalid && this.inner.value) return;
    this.inner.markAsUntouched();
  }

  /** Empty is only an error when the surface asked for the field; otherwise NANP decides. */
  private check(value: string | null): ValidationErrors | null {
    if (!value) return this.isRequired ? { required: true } : null;
    return isValidPhoneNumber(value) ? null : { invalidPhone: true };
  }
}
